import React, {useEffect, useMemo, useState} from 'react';
import {useTabHashPersistence} from "../../../../hooks/useTabHashPersistence";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {ChartererOrderList} from "./components/ChartererOrderList";
import {Badge, message, Tabs} from "antd";
import {
  OrderTypeEnum
} from "../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducerTypes";
import {OrderRequestsFilterModal} from "../../OperatorsOnly/RequestFromAgents/components/OrderRequestsFilterModal";
import {IOrderRequestFilters} from "../../OperatorsOnly/RequestFromAgents/RequestFromAgentsPage";
import {
  clearArchiveOrder,
  clearChangeEmptyLegOrderRouteWithdraw, clearChartererOrderList, getChartererOrderList,
  clearUpdateOrderFavorite,
  getOrderListCounts,
} from "../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducer";
import {
  clearChangeOrderRequestWithdraw,
} from "../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducer";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import FilterIcon from "../../../../media/icons/filter.svg?react";
import {renderSuccessModal} from "../../../../components/UI/StatusModals/renderSuccessModal";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {useSearchParams} from "react-router-dom";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {CustomSelect} from "../../../../components/UI/AntCustom/CustomSelect";
import {useIsMobile} from "../../../../hooks/useWindowSize";


const MyRequestsPageMessages = defineMessages({
  title: {
    id: "myRequestsPage.title",
    defaultMessage: "My requests",
  },
  active: {
    id: "myRequestsPage.active",
    defaultMessage: "Active",
  },
  archival: {
    id: "myRequestsPage.archival",
    defaultMessage: "Archive",
  },
  favorites: {
    id: "myRequestsPage.favorites",
    defaultMessage: "Favorites",
  },
  respondStatusChanged: {
    id: "myRequestsPage.respondStatusChanged",
    defaultMessage: "Offer status has been successfully updated.",
  },
  requestStatusChanged: {
    id: "myRequestsPage.requestStatusChanged",
    defaultMessage: "Request status has been successfully updated.",
  },
  archiveSuccess: {
    id: "myRequestsPage.archiveSuccess",
    defaultMessage: "Request has been successfully archived.",
  },
  all: {
    id: "myRequestsPage.all",
    defaultMessage: "All",
  }
})

// Используем фильтры из оператора
export type IChartererOrderRouteFilters = IOrderRequestFilters;

enum RequestFromFreightersTabKeysEnum {
  ACTIVE = 'active',
  ARCHIVE = 'archive',
  FAVORITES = 'favorites',
}

export const MyRequestsPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const archivedParams = searchParams.get('archived');
  const [activeTabKey, setActiveTabKey] = useTabHashPersistence(
    Object.values(RequestFromFreightersTabKeysEnum),
    archivedParams ? RequestFromFreightersTabKeysEnum.ARCHIVE : RequestFromFreightersTabKeysEnum.ACTIVE
  );
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<IChartererOrderRouteFilters>({});
  const [orderFilterTypes, setOrderFilterTypes] = useState<OrderTypeEnum[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const {items, totalCount, isLoading} = useAppSelector(state => state.chartererRequests.itemsObject)
  const orderListCounts = useAppSelector(state => state.chartererRequests.orderListCounts);
  const updateOrderFavorite = useAppSelector(state => state.chartererRequests.updateOrderFavorite);
  
  // Update favorites count when user changes isFavorite status
  useEffect(() => {
    if (updateOrderFavorite.isSuccess) {
      // Reload counts after favorite status change
      dispatch(getOrderListCounts({
        types: orderFilterTypes,
        airportFromId: filters.airportFromId,
        airportToId: filters.airportToId,
        onlyApproved: filters.onlyApproved,
      }));
      
      // If user is on favorites tab and removed item from favorites, reload the list
      if (activeTabKey === RequestFromFreightersTabKeysEnum.FAVORITES) {
        dispatch(getChartererOrderList({
          page: 1,
          types: orderFilterTypes,
          airportFromId: filters.airportFromId,
          airportToId: filters.airportToId,
          onlyApproved: filters.onlyApproved,
          showArchived: false,
          isFavorite: true,
        }));
      }
      
      dispatch(clearUpdateOrderFavorite());
    }
  }, [updateOrderFavorite.isSuccess, activeTabKey, orderFilterTypes, filters, dispatch])
  const {
    isLoading: isChangeEmptyLegOrderRouteWithdrawLoading,
    isSuccess: isChangeEmptyLegOrderRouteWithdrawSuccess,
    errorMessage: changeEmptyLegOrderRouteWithdrawErrorMessage,
  } = useAppSelector(state => state.chartererRequests.changeEmptyLegOrderRouteWithdraw);
  const {
    isLoading: isChangeOrderRequestWithdrawLoading,
    isSuccess: isChangeOrderRequestWithdrawSuccess,
  } = useAppSelector(state => state.freighterRequests.changeOrderRequestWithdraw);
  const {
    isLoading: isArchiveOrderRouteLoading,
    isSuccess: isArchiveOrderRouteSuccess,
    errorMessage: archiveOrderRouteErrorMessage,
  } = useAppSelector(state => state.chartererRequests.delete);


  useEffect(() => {
    // Load counts on mount
    dispatch(getOrderListCounts({
      types: orderFilterTypes,
      airportFromId: filters.airportFromId,
      airportToId: filters.airportToId,
      onlyApproved: filters.onlyApproved,
    }));

    return () => {
      dispatch(clearChartererOrderList());
      dispatch(clearChangeEmptyLegOrderRouteWithdraw());
      dispatch(clearArchiveOrder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setCurrentPage(InitialPage)

    dispatch(getChartererOrderList({
      page: InitialPage,
      types: orderFilterTypes,
      airportFromId: filters.airportFromId,
      airportToId: filters.airportToId,
      onlyApproved: filters.onlyApproved,
      // Для избранных загружаем все элементы (и архивные, и не архивные)
      // Бэкенд должен вернуть все избранные независимо от showArchived при isFavorite=true
      // Для избранных устанавливаем showArchived: false, бэкенд должен игнорировать это при isFavorite=true
      showArchived: activeTabKey === RequestFromFreightersTabKeysEnum.ARCHIVE ? true : false,
      isFavorite: activeTabKey === RequestFromFreightersTabKeysEnum.FAVORITES ? true : undefined,
    }))

    // Load counts when filters or types change
    dispatch(getOrderListCounts({
      types: orderFilterTypes,
      airportFromId: filters.airportFromId,
      airportToId: filters.airportToId,
      onlyApproved: filters.onlyApproved,
    }))
  }, [filters, orderFilterTypes, activeTabKey, dispatch])

  useEffect(() => {
    if (!isChangeEmptyLegOrderRouteWithdrawLoading) {
      if (isChangeEmptyLegOrderRouteWithdrawSuccess) {
        renderSuccessModal(intl.formatMessage(MyRequestsPageMessages.requestStatusChanged));
        dispatch(getOrderListCounts({
          types: orderFilterTypes,
          airportFromId: filters.airportFromId,
          airportToId: filters.airportToId,
          onlyApproved: filters.onlyApproved,
        }));
        dispatch(getChartererOrderList({
          page: 1,
          types: orderFilterTypes,
          airportFromId: filters.airportFromId,
          airportToId: filters.airportToId,
          onlyApproved: filters.onlyApproved,
          showArchived: activeTabKey === RequestFromFreightersTabKeysEnum.ARCHIVE,
          isFavorite: activeTabKey === RequestFromFreightersTabKeysEnum.FAVORITES ? true : undefined,
        }));
      }
      if (changeEmptyLegOrderRouteWithdrawErrorMessage)
        message.error(changeEmptyLegOrderRouteWithdrawErrorMessage);
    }
  }, [isChangeEmptyLegOrderRouteWithdrawLoading, isChangeEmptyLegOrderRouteWithdrawSuccess, changeEmptyLegOrderRouteWithdrawErrorMessage, activeTabKey, orderFilterTypes, filters, dispatch, intl])

  useEffect(() => {
    if (!isChangeOrderRequestWithdrawLoading && isChangeOrderRequestWithdrawSuccess) {
      dispatch(getOrderListCounts({
        types: orderFilterTypes,
        airportFromId: filters.airportFromId,
        airportToId: filters.airportToId,
        onlyApproved: filters.onlyApproved,
      }));
      dispatch(getChartererOrderList({
        page: 1,
        types: orderFilterTypes,
        airportFromId: filters.airportFromId,
        airportToId: filters.airportToId,
        onlyApproved: filters.onlyApproved,
        showArchived: activeTabKey === RequestFromFreightersTabKeysEnum.ARCHIVE,
        isFavorite: activeTabKey === RequestFromFreightersTabKeysEnum.FAVORITES ? true : undefined,
      }));
      dispatch(clearChangeOrderRequestWithdraw());
    }
  }, [isChangeOrderRequestWithdrawLoading, isChangeOrderRequestWithdrawSuccess, activeTabKey, orderFilterTypes, filters, dispatch])

  useEffect(() => {
    if (!isArchiveOrderRouteLoading) {
      if (isArchiveOrderRouteSuccess) {
        renderSuccessModal(intl.formatMessage(MyRequestsPageMessages.archiveSuccess));
        // Refresh counts and list after archiving
        dispatch(getOrderListCounts({
          types: orderFilterTypes,
          airportFromId: filters.airportFromId,
          airportToId: filters.airportToId,
          onlyApproved: filters.onlyApproved,
        }));
        dispatch(getChartererOrderList({
          page: 1,
          types: orderFilterTypes,
          airportFromId: filters.airportFromId,
          airportToId: filters.airportToId,
          onlyApproved: filters.onlyApproved,
          showArchived: activeTabKey === RequestFromFreightersTabKeysEnum.ARCHIVE,
          isFavorite: activeTabKey === RequestFromFreightersTabKeysEnum.FAVORITES ? true : undefined,
        }));
      }
      if (archiveOrderRouteErrorMessage)
        message.error(archiveOrderRouteErrorMessage);
    }
  }, [isArchiveOrderRouteLoading, isArchiveOrderRouteSuccess, archiveOrderRouteErrorMessage, activeTabKey, orderFilterTypes, filters, dispatch, intl])

  const loadMore = () => {
    dispatch(getChartererOrderList({
      page: currentPage + 1,
      types: orderFilterTypes,
      airportFromId: filters.airportFromId,
      airportToId: filters.airportToId,
      onlyApproved: filters.onlyApproved,
      showArchived: activeTabKey === RequestFromFreightersTabKeysEnum.ARCHIVE,
      // Не передаем isFavorite в запрос, фильтруем на клиенте
    }));

    setCurrentPage(prev => prev + 1);
  }

  const tabList = useMemo(() => [
    {
      key: RequestFromFreightersTabKeysEnum.ACTIVE,
      label: `${intl.formatMessage(MyRequestsPageMessages.active)} (${orderListCounts.active})`,
      children: <ChartererOrderList isFavorite={false} expectedCount={orderListCounts.active}/>
    },
    {
      key: RequestFromFreightersTabKeysEnum.ARCHIVE,
      label: `${intl.formatMessage(MyRequestsPageMessages.archival)} (${orderListCounts.archival})`,
      children: <ChartererOrderList isFavorite={false} expectedCount={orderListCounts.archival}/>
    },
    {
      key: RequestFromFreightersTabKeysEnum.FAVORITES,
      label: `${intl.formatMessage(MyRequestsPageMessages.favorites)} (${orderListCounts.favorites})`,
      children: <ChartererOrderList isFavorite={true} expectedCount={orderListCounts.favorites}/>
    },
  ], [intl, orderListCounts]);

  const extra = <>
    <CustomSelect mode={"tags"} defaultValue={[]}
                  placement={'bottomRight'}
                  style={{minWidth: isMobile ? 88 : 120, width: isMobile ? 88 : "unset"}}
                  showArrow={true}
                  allowClear={true}
                  dropdownMatchSelectWidth={false}
                  onChange={(types) => setOrderFilterTypes((types as string[]).map(Number) as OrderTypeEnum[])} placeholder={"All"} options={[
      {label: "One way", value: String(OrderTypeEnum.ONE_WAY)},
      {label: "Round Trip", value: String(OrderTypeEnum.ROUND_TRIP)},
      {label: "Multi Leg", value: String(OrderTypeEnum.MULTI_LEG)},
      {label: "Empty Leg", value: String(OrderTypeEnum.EMPTY_LEG)},
    ]}/>
    <Badge count={Object.values(filters).filter(v => v).length}>
      <CustomButton onClick={() => setIsFiltersModalOpen(true)} style={{marginLeft: 8}} heightSize={'m'}>
        <FilterIcon/>
      </CustomButton>
    </Badge>
  </>

  return (
    <PageWrapper>
      <CustomCard title={intl.formatMessage(MyRequestsPageMessages.title)} width={'max'} isContainerCard={true}
                  extra={isMobile ? extra : null}
                  onTabChange={(key: string) => {
                    setActiveTabKey(key as RequestFromFreightersTabKeysEnum)
                  }}>
        <Tabs 
          tabBarExtraContent={isMobile ? null : extra} 
          activeKey={activeTabKey} 
          onChange={(key: string) => {
            setActiveTabKey(key as RequestFromFreightersTabKeysEnum);
          }}
          destroyInactiveTabPane
          items={tabList.map(t => ({
            key: t.key,
            label: t.label,
            children: t.children,
            forceRender: false,
          }))}
        />

        {items.length > 0 && totalCount > items.length &&
          <CustomButton
            loading={isLoading}
            type={'primary'}
            onClick={loadMore}
          >
            {intl.formatMessage(CommonFormMessages.showMore)}
          </CustomButton>}
      </CustomCard>

      {isFiltersModalOpen &&
        <OrderRequestsFilterModal 
          isModalVisible={isFiltersModalOpen} 
          setIsModalVisible={setIsFiltersModalOpen}
          filters={filters} 
          setFilters={setFilters}
          title={intl.formatMessage({ id: 'orderRequestsFilterModal.responsesFilterTitle', defaultMessage: 'Response filter' })}
          showClearAll={true}
        />}
    </PageWrapper>
  );
};


